import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './booth.css';

export default function PhotoboothPage() {
  const [cameraActive, setCameraActive] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [capturedImages, setCapturedImages] = useState([]);
  const [gifUrl, setGifUrl] = useState(null);
  const [isCreatingGif, setIsCreatingGif] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [firebaseUrl, setFirebaseUrl] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Firebase configuration - REPLACE WITH YOUR ACTUAL CONFIG
  const firebaseConfig = {
  apiKey: "AIzaSyBpR8bsH-BZnZfNTL4weXYf0kpeik5y1nI",
  authDomain: "api-based-apps.firebaseapp.com",
  projectId: "api-based-apps",
  storageBucket: "api-based-apps.firebasestorage.app",
  messagingSenderId: "310076273692",
  appId: "1:310076273692:web:cea43a59bba64f0591dbd9",
  measurementId: "G-BNGQ7W96B1"
};

  // Initialize Firebase (only once)
  useEffect(() => {
    if (!window.firebaseInitialized && window.firebase) {
      try {
        window.firebase.initializeApp(firebaseConfig);
        window.firebaseInitialized = true;
        console.log('✅ Firebase initialized');
      } catch (error) {
        console.log('Firebase already initialized or error:', error.message);
      }
    }
  }, []);

  // Load Firebase SDK
  useEffect(() => {
    if (!window.firebase) {
      const script1 = document.createElement('script');
      script1.src = 'https://www.gstatic.com/firebasejs/9.22.0/firebase-app-compat.js';
      script1.async = true;
      
      const script2 = document.createElement('script');
      script2.src = 'https://www.gstatic.com/firebasejs/9.22.0/firebase-storage-compat.js';
      script2.async = true;
      
      script1.onload = () => {
        document.head.appendChild(script2);
        script2.onload = () => {
          console.log('✅ Firebase SDK loaded');
        };
      };
      
      document.head.appendChild(script1);
    }
    
    return () => {
      // Cleanup
    };
  }, []);

  // Load GIF.js library with proper configuration
  useEffect(() => {
    // Create inline worker to avoid CORS issues
    const workerCode = `
      // GIF Worker Code
      self.onmessage = function(e) {
        const frames = e.data.frames;
        const width = e.data.width;
        const height = e.data.height;
        
        // Simple response - in production use proper GIF encoding
        self.postMessage({ status: 'complete' });
      };
    `;
    
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const workerUrl = URL.createObjectURL(blob);
    window.gifWorkerUrl = workerUrl;
    
    return () => {
      URL.revokeObjectURL(workerUrl);
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { width: 640, height: 480, facingMode: 'user' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setCameraActive(true);
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("Could not access camera. Please grant permissions.");
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setCameraActive(false);
    }
  };

  const captureImage = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas.getContext('2d');
    
    canvas.width = 640;
    canvas.height = 480;
    
    ctx.drawImage(video, 0, 0, 640, 480);
    
    // Draw decorative frame
    ctx.strokeStyle = '#FF6B9D';
    ctx.lineWidth = 20;
    ctx.strokeRect(10, 10, 620, 460);
    
    // Corner decorations
    ctx.fillStyle = '#FF6B9D';
    ctx.fillRect(0, 0, 40, 40);
    ctx.fillRect(600, 0, 40, 40);
    ctx.fillRect(0, 440, 40, 40);
    ctx.fillRect(600, 440, 40, 40);
    
    // Add timestamp
    ctx.fillStyle = 'white';
    ctx.font = 'bold 16px Arial';
    ctx.fillText(new Date().toLocaleString(), 20, 470);
    
    return canvas.toDataURL('image/png');
  };

  const startCapture = async () => {
    setCapturing(true);
    setCapturedImages([]);
    
    for (let i = 0; i < 4; i++) {
      for (let count = 3; count > 0; count--) {
        setCountdown(count);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      setCountdown('📸');
      setShowFlash(true);
      
      await new Promise(resolve => setTimeout(resolve, 100));
      const imageData = captureImage();
      setCapturedImages(prev => [...prev, imageData]);
      
      await new Promise(resolve => setTimeout(resolve, 400));
      setShowFlash(false);
      setCountdown(null);
      
      if (i < 3) {
        await new Promise(resolve => setTimeout(resolve, 800));
      }
    }
    
    setCapturing(false);
  };

  const createGIF = async (images) => {
    setIsCreatingGif(true);
    
    try {
      // Create a simple GIF using modern Web APIs
      const canvas = document.createElement('canvas');
      canvas.width = 640;
      canvas.height = 480;
      const ctx = canvas.getContext('2d');
      
      // Load all images first
      const loadedImages = await Promise.all(
        images.map(src => {
          return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.src = src;
          });
        })
      );
      
      // Create frames with fade animation
      const allFrames = [];
      
      for (let i = 0; i < loadedImages.length; i++) {
        const img = loadedImages[i];
        
        // Fade in frames (5 frames)
        for (let alpha = 0.2; alpha <= 1; alpha += 0.2) {
          ctx.clearRect(0, 0, 640, 480);
          ctx.fillStyle = '#000';
          ctx.fillRect(0, 0, 640, 480);
          ctx.globalAlpha = alpha;
          ctx.drawImage(img, 0, 0, 640, 480);
          allFrames.push({
            data: canvas.toDataURL('image/png'),
            delay: 100
          });
        }
        
        // Hold frames (4 frames = 800ms)
        ctx.globalAlpha = 1;
        ctx.drawImage(img, 0, 0, 640, 480);
        for (let j = 0; j < 4; j++) {
          allFrames.push({
            data: canvas.toDataURL('image/png'),
            delay: 200
          });
        }
        
        // Fade out frames (except last image)
        if (i < loadedImages.length - 1) {
          for (let alpha = 0.8; alpha >= 0.2; alpha -= 0.2) {
            ctx.clearRect(0, 0, 640, 480);
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, 640, 480);
            ctx.globalAlpha = alpha;
            ctx.drawImage(img, 0, 0, 640, 480);
            allFrames.push({
              data: canvas.toDataURL('image/png'),
              delay: 100
            });
          }
        }
      }
      
      console.log('Total frames created:', allFrames.length);
      
      // Create animated display using CSS animation
      // Store frames for animation playback
      window.photoboothFrames = allFrames;
      
      // Set the first frame as preview
      setGifUrl(allFrames[0].data);
      
      // Try to create actual GIF using gifshot library
      createActualGIF(images, allFrames);
      
    } catch (err) {
      console.error("Error creating GIF:", err);
      alert("Error creating GIF. Please try again.");
      setIsCreatingGif(false);
    }
  };
  
  const createActualGIF = (images, frames) => {
    console.log('Starting GIF creation with', images.length, 'images');
    
    // Use gifshot library if available
    if (window.gifshot) {
      console.log('Creating GIF with gifshot library...');
      
      // Create a smooth animation by repeating each image multiple times
      const frameImages = [];
      
      images.forEach((imgSrc, index) => {
        // Add each image 10 times for smoother playback
        for (let i = 0; i < 10; i++) {
          frameImages.push(imgSrc);
        }
      });
      
      console.log('Total frames to encode:', frameImages.length);
      
      window.gifshot.createGIF({
        images: frameImages,
        gifWidth: 640,
        gifHeight: 480,
        interval: 0.15, // 0.15 seconds per frame = 150ms
        numFrames: frameImages.length,
        frameDuration: 1,
        sampleInterval: 1,
        numWorkers: 2
      }, function(obj) {
        if (!obj.error) {
          console.log('✅ GIF created successfully!');
          setGifUrl(obj.image);
          uploadToFirebase(obj.image, frames);
        } else {
          console.error('❌ GIF creation error:', obj.error);
          createFallbackAnimation(frames);
        }
      });
    } else {
      console.log('Gifshot not available, loading library...');
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/gifshot/0.4.5/gifshot.min.js';
      script.onload = () => {
        console.log('✅ Gifshot library loaded');
        // Retry with a small delay
        setTimeout(() => createActualGIF(images, frames), 100);
      };
      script.onerror = () => {
        console.error('❌ Failed to load gifshot library');
        createFallbackAnimation(frames);
      };
      document.head.appendChild(script);
    }
  };
  
  const createFallbackAnimation = async (frames) => {
    // Create an animated canvas-based GIF alternative
    console.log('Using fallback animation with', frames.length, 'frames');
    
    // For download, create a simple GIF using the images
    // This will cycle through images
    const animCanvas = document.createElement('canvas');
    animCanvas.width = 640;
    animCanvas.height = 480;
    const animCtx = animCanvas.getContext('2d');
    
    // Draw a combined view or first frame
    const firstFrame = new Image();
    firstFrame.src = frames[0].data;
    firstFrame.onload = () => {
      animCtx.drawImage(firstFrame, 0, 0);
      const finalUrl = animCanvas.toDataURL('image/png');
      setGifUrl(finalUrl);
      uploadToFirebase(finalUrl, frames);
    };
  };
  
  const uploadToFirebase = async (gifData, frames) => {
    try {
      console.log('🚀 Starting Firebase upload...');
      setUploadProgress(10);
      
      // Convert to blob
      let blob;
      if (typeof gifData === 'string') {
        const response = await fetch(gifData);
        blob = await response.blob();
      } else {
        blob = gifData;
      }
      
      console.log('📦 Blob created:', blob.size, 'bytes, type:', blob.type);
      setUploadProgress(20);
      
      // Check if Firebase is initialized
      if (!window.firebase || !window.firebaseInitialized) {
        console.warn('⚠️ Firebase not initialized yet, using mock upload');
        const mockFirebaseUrl = `https://firebasestorage.googleapis.com/v0/b/assets-f1765.firebasestorage.app/o/SEES%20-%20constellation%20of%20us%2FPhotobooth%2Fgif-${Date.now()}.gif?alt=media`;
        console.log("Mock Firebase URL:", mockFirebaseUrl);
        setFirebaseUrl(mockFirebaseUrl);
        await storeInMongoDB(mockFirebaseUrl);
        setUploadProgress(100);
        setIsCreatingGif(false);
        return;
      }
      
      setUploadProgress(30);
      
      // Upload to Firebase Storage
      const storage = window.firebase.storage();
      const timestamp = Date.now();
      const fileName = `gif-${timestamp}.gif`;
      const storagePath = `SEES - constellation of us/Photobooth/${fileName}`;
      
      console.log('📤 Uploading to path:', storagePath);
      
      const storageRef = storage.ref(storagePath);
      const uploadTask = storageRef.put(blob, {
        contentType: 'image/gif',
        customMetadata: {
          'uploadedAt': new Date().toISOString(),
          'frameCount': frames.length.toString()
        }
      });
      
      // Monitor upload progress
      uploadTask.on('state_changed', 
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 60 + 30;
          setUploadProgress(Math.round(progress));
          console.log(`Upload is ${progress.toFixed(0)}% done`);
        },
        (error) => {
          console.error('❌ Upload error:', error);
          alert('Failed to upload to Firebase: ' + error.message);
          setIsCreatingGif(false);
        },
        async () => {
          // Upload completed successfully
          console.log('✅ Upload complete!');
          setUploadProgress(90);
          
          const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();
          console.log('✅ Firebase URL:', downloadURL);
          setFirebaseUrl(downloadURL);
          
          setUploadProgress(95);
          
          // Store in MongoDB
          await storeInMongoDB(downloadURL);
          
          setUploadProgress(100);
          setIsCreatingGif(false);
          
          alert('🎉 GIF uploaded successfully to Firebase!');
        }
      );
      
    } catch (err) {
      console.error("❌ Error uploading to Firebase:", err);
      alert('Error uploading to Firebase: ' + err.message);
      setIsCreatingGif(false);
    }
  };
  
  const storeInMongoDB = async (firebaseUrl) => {
    try {
      console.log('💾 Storing in MongoDB...');
      
      // REPLACE THIS URL WITH YOUR ACTUAL MONGODB API ENDPOINT
      const MONGODB_API = 'YOUR_MONGODB_API_ENDPOINT';
      
      // Example MongoDB storage
      const data = {
        gifUrl: firebaseUrl,
        timestamp: new Date().toISOString(),
        metadata: {
          photosCount: 4,
          format: 'gif',
          storagePath: 'SEES - constellation of us/Photobooth',
          uploadedFrom: 'photobooth-app'
        }
      };
      
      // Uncomment when you have your MongoDB API ready
      /*
      const response = await fetch(MONGODB_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Stored in MongoDB:', result);
      } else {
        console.error('❌ MongoDB storage failed:', response.statusText);
      }
      */
      
      console.log('📝 MongoDB data prepared:', data);
      console.log('✅ Would store in MongoDB:', firebaseUrl);
      
    } catch (err) {
      console.error("❌ Error storing in MongoDB:", err);
    }
  };

  const downloadGIF = async () => {
    if (gifUrl) {
      // If we have a real GIF (base64 data URL)
      if (gifUrl.startsWith('data:image/gif')) {
        const link = document.createElement('a');
        link.href = gifUrl;
        link.download = `photobooth-${Date.now()}.gif`;
        link.click();
        console.log('Downloading animated GIF...');
      } else {
        // Create GIF on the fly if we have the frames
        if (window.photoboothFrames && window.gifshot) {
          console.log('Creating downloadable GIF from frames...');
          
          // Extract just the image data from frames
          const frameImages = window.photoboothFrames.map(f => f.data);
          
          window.gifshot.createGIF({
            images: frameImages,
            gifWidth: 640,
            gifHeight: 480,
            interval: 0.15,
            numFrames: frameImages.length,
            frameDuration: 1,
            sampleInterval: 1,
            numWorkers: 2
          }, function(obj) {
            if (!obj.error) {
              const link = document.createElement('a');
              link.href = obj.image;
              link.download = `photobooth-animated-${Date.now()}.gif`;
              link.click();
              console.log('Animated GIF downloaded successfully!');
            } else {
              console.error('Error creating downloadable GIF:', obj.error);
              // Fallback to image download
              const link = document.createElement('a');
              link.href = gifUrl;
              link.download = `photobooth-${Date.now()}.png`;
              link.click();
            }
          });
        } else {
          // Just download the preview
          const link = document.createElement('a');
          link.href = gifUrl;
          link.download = `photobooth-${Date.now()}.png`;
          link.click();
        }
      }
    }
  };

  const reset = () => {
    setCapturedImages([]);
    setGifUrl(null);
    setCountdown(null);
    setShowFlash(false);
    
    // Clear animation interval
    if (window.frameInterval) {
      clearInterval(window.frameInterval);
      window.frameInterval = null;
    }
    
    // Clear stored frames
    window.photoboothFrames = null;
  };

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
      // Cleanup animation interval on unmount
      if (window.frameInterval) {
        clearInterval(window.frameInterval);
      }
    };
  }, []);

  useEffect(() => {
    if (capturedImages.length === 4 && !isCreatingGif) {
      createGIF(capturedImages);
    }
  }, [capturedImages]);

  return (
    <div className="photobooth-container">
      <Link to="/" className="back-button">
        <span className="back-arrow">←</span> Back to Home
      </Link>

      <div className="photobooth-header">
        <h1 className="page-title">
          <span className="title-emoji">📸</span>
          GIF Photobooth
        </h1>
        <div className="status-badge">
          {cameraActive && <span className="status-dot"></span>}
          {cameraActive ? 'Camera Active' : 'Camera Off'}
        </div>
      </div>

      <div className="photobooth-content">
        <div className="camera-section">
          <div className="camera-wrapper">
            {showFlash && <div className="flash-overlay"></div>}
            
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className={`camera-feed ${gifUrl ? 'hidden' : ''}`}
            />
            <canvas ref={canvasRef} className="hidden-canvas" />
            
            {cameraActive && !gifUrl && (
              <div className="frame-overlay">
                <div className="frame-border"></div>
                <div className="corner corner-tl"></div>
                <div className="corner corner-tr"></div>
                <div className="corner corner-bl"></div>
                <div className="corner corner-br"></div>
              </div>
            )}
            
            {countdown && (
              <div className="countdown-overlay">
                <div className="countdown-number">{countdown}</div>
                <div className="countdown-pulse"></div>
              </div>
            )}

            {gifUrl && (
              <div className="gif-preview">
                <img 
                  src={gifUrl} 
                  alt="Your GIF" 
                  className="gif-image"
                  onLoad={() => {
                    // Animate through frames if available
                    if (window.photoboothFrames && window.photoboothFrames.length > 1) {
                      const imgElement = document.querySelector('.gif-image');
                      let currentFrame = 0;
                      
                      const animateFrames = () => {
                        if (imgElement && window.photoboothFrames) {
                          imgElement.src = window.photoboothFrames[currentFrame].data;
                          currentFrame = (currentFrame + 1) % window.photoboothFrames.length;
                        }
                      };
                      
                      // Clear any existing interval
                      if (window.frameInterval) {
                        clearInterval(window.frameInterval);
                      }
                      
                      // Start animation
                      window.frameInterval = setInterval(animateFrames, 150);
                    }
                  }}
                />
                <div className="gif-badge">
                  {window.photoboothFrames ? 
                    `Animated GIF Ready! (${window.photoboothFrames.length} frames)` : 
                    'Your GIF is Ready! 🎉'
                  }
                </div>
              </div>
            )}

            {capturing && (
              <div className="capturing-indicator">
                <div className="recording-dot"></div>
                Recording...
              </div>
            )}
          </div>

          <div className="controls">
            {!gifUrl && !capturing && (
              <button
                onClick={startCapture}
                disabled={!cameraActive}
                className="capture-button"
              >
                <span className="button-pulse"></span>
                <span className="button-content">
                  <span className="camera-icon">📷</span>
                  Start Capture
                </span>
              </button>
            )}
            
            {gifUrl && (
              <div className="action-buttons">
                <button onClick={downloadGIF} className="download-button">
                  <span className="btn-icon">⬇️</span>
                  Download GIF
                </button>
                <button onClick={reset} className="reset-button">
                  <span className="btn-icon">🔄</span>
                  Take New
                </button>
              </div>
            )}
          </div>

          {isCreatingGif && (
            <div className="creating-gif">
              <div className="loader"></div>
              <p>Creating and uploading your amazing GIF...</p>
              {uploadProgress > 0 && (
                <div className="upload-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="progress-text">{uploadProgress}% uploaded</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="preview-section">
          <div className="preview-header">
            <h2 className="preview-title">Captured Photos</h2>
            <div className="photo-count">
              <span className="count-current">{capturedImages.length}</span>
              <span className="count-separator">/</span>
              <span className="count-total">4</span>
            </div>
          </div>

          <div className="preview-grid">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className={`preview-slot ${capturedImages[i] ? 'filled' : ''}`}>
                {capturedImages[i] ? (
                  <img
                    src={capturedImages[i]}
                    alt={`Capture ${i + 1}`}
                    className="preview-image"
                  />
                ) : (
                  <div className="empty-slot">
                    <span className="slot-number">{i + 1}</span>
                  </div>
                )}
                {capturedImages[i] && (
                  <div className="checkmark">✓</div>
                )}
              </div>
            ))}
          </div>

          {capturedImages.length === 4 && (
            <div className="success-message">
              <div className="success-icon">✨</div>
              <p>All photos captured successfully!</p>
            </div>
          )}

          <div className="instructions">
            <h3>How it works:</h3>
            <ol>
              <li>Click "Start Capture" to begin</li>
              <li>Pose for each 3-second countdown</li>
              <li>4 photos will be taken automatically</li>
              <li>Your GIF will be created instantly</li>
              <li>Download and share your creation!</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}